import { IntlShape } from "react-intl";

import { TerritoryLevel } from "@mapmycustomers/shared";
import Territory, { TerritoryDetail } from "@mapmycustomers/shared/types/entity/Territory";
import AreaRegion, { AreaMetaFile } from "@mapmycustomers/shared/types/territory/AreaRegion";
import AreaType from "@mapmycustomers/shared/types/territory/AreaType";

import getBoundaryUrl from "@app/scene/map/utils/getBoundaryUrl";

import { downloadFile } from "./file";
import { getAreaTypeDisplayName } from "./ui";

type Row = [name: Territory["name"], admName: string];

const getDataRowsForTerritoryDetail = (
  territoryName: Territory["name"],
  territoryDetail: TerritoryDetail,
  nameByAreaRegion: Map<AreaRegion["id"], string> | undefined
): Row[] => {
  const { adm1, adm2, adm3 } = territoryDetail;
  if (adm1.length) {
    return adm1.map((value) => [territoryName, value]);
  } else if (adm2.length) {
    return adm2.map((value) => [territoryName, nameByAreaRegion?.get(value) ?? value]);
  } else if (adm3.length) {
    return adm3.map((value) => [territoryName, value]);
  }
  return [];
};

const INITIAL_STATE: AreaMetaFile = {
  files: [],
  regions: [],
};

const downloadAreaTypeMetaData = async (areaType: AreaType) => {
  return fetch(getBoundaryUrl("meta", areaType))
    .then((response) => response.json() as Promise<AreaMetaFile>)
    .catch(() => {
      return INITIAL_STATE;
    });
};

const downloadTerritoryBoundsAsCsv = async (intl: IntlShape, territories: Territory[]) => {
  const areaTerritories = territories.filter(
    ({ territoryDetail }) => territoryDetail.type !== "points"
  );

  const areaTypes = Array.from(
    new Set(
      areaTerritories
        .map(({ territoryDetail }) => territoryDetail.type as AreaType)
        .filter((areaType) => areaType.endsWith(TerritoryLevel.ADM2))
    )
  );

  const areaTypesMetadata: Map<AreaType, Map<AreaRegion["id"], string>> = new Map();

  await Promise.all(
    areaTypes.map(async (areaType) => {
      const metaData = await downloadAreaTypeMetaData(areaType);
      const nameByCode = new Map(metaData.regions.map(({ id, name }) => [id, name]));
      areaTypesMetadata.set(areaType, nameByCode);
    })
  );

  const areaTypeWithBounds: Map<TerritoryDetail["type"], [Territory["name"], string][]> =
    areaTerritories.reduce((result, territory) => {
      const type = territory.territoryDetail.type;
      const dataRows: Row[] = getDataRowsForTerritoryDetail(
        territory.name,
        territory.territoryDetail,
        areaTypesMetadata.get(type as AreaType)
      );
      if (dataRows.length) {
        const currentDataRows: Row[] | undefined = result.get(type);
        result.set(type, currentDataRows ? currentDataRows.concat(dataRows) : dataRows);
      }
      return result;
    }, new Map());

  areaTypeWithBounds.forEach(function (value, key) {
    const areaTypeDisplayName = getAreaTypeDisplayName(intl, key as AreaType);
    const headerRow = ["Territory", areaTypeDisplayName];
    const csvText = [headerRow.join(","), ...value.map((row) => row.join(","))].join("\n");

    const blob = new Blob([csvText], { type: "text/plain" });
    const fileName = `${areaTypeDisplayName}.csv`;
    downloadFile(fileName, blob);
  });
};

export default downloadTerritoryBoundsAsCsv;
